/**
 * bankChannel.js - Feb 2026 (iter1086h)
 *
 * Frontend mirror of `backend/utils/bank_channel.py`. Do NOT let the two
 * drift - if you add a source string to one, add it to the other.
 *
 * Collapses the many `source` values on `bank_feed_transactions` rows
 * into three stable channels:
 *
 *   - open_banking     : Mono / Plaid / Stitch / Teller / Tink live pull
 *   - statement_upload : CSV / PDF / Excel / OCR upload of a real statement
 *   - manual_entry     : User typed the row in by hand
 *
 * Every UI chip, filter, and cutover reconciliation view should reason
 * in channels, not raw source strings.
 */

export const CHANNEL_OPEN_BANKING = 'open_banking';
export const CHANNEL_STATEMENT    = 'statement_upload';
export const CHANNEL_MANUAL       = 'manual_entry';

const _OPEN_BANKING_SOURCES = new Set([
  'open_banking_mono', 'mono',
  'plaid', 'stitch', 'teller', 'tink',
  'open_banking',
  'bank_feed_deterministic_charge',
]);

const _STATEMENT_SOURCES = new Set([
  'statement_upload', 'file_upload', 'pdf_upload', 'csv_upload',
  'excel_upload', 'ocr_upload', 'cash_advance_upload',
  'bank_feed', 'reconciliation',
]);

const _MANUAL_SOURCES = new Set([
  'manual_entry', 'manual', 'manual_transaction',
]);

/**
 * Return one of `open_banking | statement_upload | manual_entry`.
 * Never throws - unknown strings collapse to `statement_upload`.
 */
export function deriveChannel(source) {
  const s = String(source || '').trim().toLowerCase();
  if (!s) return CHANNEL_STATEMENT;
  if (_OPEN_BANKING_SOURCES.has(s)) return CHANNEL_OPEN_BANKING;
  if (_MANUAL_SOURCES.has(s))       return CHANNEL_MANUAL;
  if (_STATEMENT_SOURCES.has(s))    return CHANNEL_STATEMENT;
  return CHANNEL_STATEMENT;
}

/**
 * Display metadata (label + tone) for a channel chip. Used by
 * ChannelChip.jsx and the filter dropdown so the two views agree.
 */
export const CHANNEL_META = {
  [CHANNEL_OPEN_BANKING]: {
    label: 'Bank Sync',
    // NOTE: `short` here is only used when ChannelChip cannot resolve a
    // provider-specific short label from the row's `source`. Kept as a
    // neutral fallback per Feb-2026 UX audit - the row-level chip no
    // longer names underlying providers.
    short: 'SYNC',
    tone: 'bg-[#108981]/12 text-[#108981] border-[#108981]/30',
    title: 'Live pull from your connected bank',
  },
  [CHANNEL_STATEMENT]: {
    label: 'Statement Upload',
    short: 'STMT',
    tone: 'bg-blue-50 text-blue-700 border-blue-200',
    title: 'From an uploaded statement (CSV / PDF / Excel)',
  },
  [CHANNEL_MANUAL]: {
    label: 'Manual Entry',
    short: 'MANUAL',
    tone: 'bg-slate-100 text-slate-700 border-slate-300',
    title: 'Typed in manually by a user',
  },
};

export function channelLabel(channel) {
  return CHANNEL_META[channel]?.label || channel;
}
